import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurriculum } from "@/context/CurriculumContext";
import { SUBJECT_META_ALL } from "@/lib/scale";
import { toast } from "sonner";
import { ArrowLeft, Printer, Loader2, Sprout, Sparkles, Target, Share2, Check, Wand2 } from "lucide-react";

const T = { green: "#B3E5A1", greenDeep: "#7FB069", slate: "#44546A", grey: "#F2F2F2" };
const PROGRESS_LABEL = {
  not_achieved: { label: "Just starting", color: "#C97064" },
  acquiring: { label: "Getting there", color: "#b07d3a" },
  becoming_fluent: { label: "Almost there", color: "#6C8EBF" },
  achieved: { label: "Achieved", color: "#5d8a73" },
};

export default function ParentReport() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const curriculum = useCurriculum();
  const [student, setStudent] = useState(null);
  const [summary, setSummary] = useState("");
  const [school, setSchool] = useState({ name: user?.school_name || "", logo: user?.school_logo || "" });
  const [generating, setGenerating] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [refining, setRefining] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(null); // {token, access_code}
  const [term, setTerm] = useState("");

  const load = useCallback(() => {
    api.get(`/students/${studentId}`).then((r) => setStudent(r.data)).catch(() => navigate("/"));
    api.get(`/support-docs/${studentId}/parent_summary`)
      .then((r) => setSummary(r.data?.content?.text || "")).catch(() => {});
    api.get("/auth/me").then((r) => setSchool({ name: r.data?.school_name || "", logo: r.data?.school_logo || "" })).catch(() => {});
  }, [studentId, navigate]);
  useEffect(() => { load(); }, [load]);

  const saveSummary = async () => {
    setSavingSummary(true);
    try {
      await api.put(`/support-docs/${studentId}/parent_summary`, { content: { text: summary } });
      toast.success("Summary saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSummary(false);
    }
  };

  const refine = async (instruction) => {
    setRefining(true);
    try {
      const { data } = await api.post("/ai/refine", { text: summary, instruction });
      setSummary(data.text);
      toast.success("Summary adjusted — review before saving");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRefining(false);
    }
  };

  const generate = async () => {
    if (summary && !window.confirm("Regenerate will replace your current summary (including any edits). Continue?")) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/suggest/parent-summary/${studentId}`);
      setSummary(data.text);
      toast.success("Parent summary ready — review and edit before printing");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const share = async () => {
    setSharing(true);
    try {
      const expires_at = (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })();
      const { data } = await api.post("/parent-shares", { student_id: studentId, doc_type: "parent_report", expires_at });
      await navigator.clipboard.writeText(`${window.location.origin}/p/${data.token}`).catch(() => {});
      setShared({ token: data.token, access_code: data.access_code });
      toast.success(`Secure parent link copied. Access code: ${data.access_code}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSharing(false);
    }
  };

  if (!student || !curriculum)
    return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const subjects = curriculum.subjects;
  const targets = (student.targets || []).filter((t) => t.priority);
  let generateLabel = "Generate with AI";
  if (generating) generateLabel = "Writing…";
  else if (summary) generateLabel = "Regenerate summary";

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4">
      {/* Toolbar */}
      <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 mb-6 no-print flex-wrap">
        <button data-testid="report-back-btn" onClick={() => navigate(`/student/${studentId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition">
          <ArrowLeft size={16} /> Back to profile
        </button>
        <div className="flex items-center gap-2">
          <button data-testid="report-generate-btn" onClick={generate} disabled={generating}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-50"
            style={{ background: `linear-gradient(135deg,${T.greenDeep},#5d8a5a)` }}>
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generateLabel}
          </button>
          <button data-testid="report-share-btn" onClick={share} disabled={sharing || !summary}
            title={!summary ? "Generate the summary first" : "Create a secure read-only parent link"}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-[#7FB069] transition disabled:opacity-50">
            {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} Share with parents
          </button>
          <button data-testid="report-print-btn" onClick={() => window.print()}
            className="text-white rounded-full px-5 py-2 font-semibold font-['Outfit'] transition flex items-center gap-2"
            style={{ backgroundColor: T.slate }}>
            <Printer size={17} /> Print / Save PDF
          </button>
        </div>
      </div>

      {shared && (
        <div data-testid="share-result" className="no-print max-w-3xl mx-auto mb-6 -mt-2 flex items-center gap-3 flex-wrap rounded-xl bg-white border border-[#7FB069]/40 px-4 py-3">
          <span className="text-sm text-slate-600">Parent link ready. Access code:</span>
          <span data-testid="share-access-code" className="font-mono font-bold tracking-widest text-slate-800 bg-[#F2F2F2] rounded-lg px-3 py-1">{shared.access_code}</span>
          <button data-testid="copy-share-link-btn"
            onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/p/${shared.token}`); toast.success("Link copied"); }}
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold rounded-full border border-slate-200 px-3 py-1.5 text-slate-600 hover:border-[#7FB069] transition">
            <Share2 size={14} /> Copy link
          </button>
        </div>
      )}

      {/* Sheet */}
      <div className="print-area max-w-3xl mx-auto bg-white rounded-2xl shadow-[0_4px_24px_rgba(42,59,71,0.06)] overflow-hidden">
        {/* Branded header */}
        <div className="px-8 sm:px-10 pt-8 pb-5 flex items-center justify-between" style={{ borderBottom: `3px solid ${T.greenDeep}` }}>
          <div className="flex items-center gap-3">
            {school.logo ? (
              <img src={school.logo} alt="School crest" className="h-12 w-12 object-contain rounded-lg" data-testid="report-school-logo" />
            ) : (
              <span className="h-11 w-11 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: T.greenDeep }}><Sprout size={20} /></span>
            )}
            <div>
              <p className="font-['Outfit'] font-bold text-lg leading-tight" style={{ color: T.slate }} data-testid="report-school-name">
                {school.name || "BloomTrack"}
              </p>
              <p className="text-xs text-slate-400">End-of-Term Progress Report</p>
            </div>
          </div>
          <div className="text-right no-print">
            <input data-testid="report-term-input" value={term} onChange={(e) => setTerm(e.target.value)}
              placeholder="e.g. Summer Term 2026"
              className="text-sm text-right border-b border-slate-200 pb-1 outline-none focus:border-[#7FB069] bg-transparent" />
          </div>
          {term && <p className="hidden print:block text-sm text-slate-500">{term}</p>}
        </div>

        <div className="px-8 sm:px-10 py-7">
          <h1 className="font-['Outfit'] text-3xl font-bold" style={{ color: T.slate }}>{student.name}</h1>
          <p className="text-slate-500 mt-1">{student.class_level}{student.class_name ? ` · ${student.class_name}` : ""}{term ? ` · ${term}` : ""}</p>

          {/* AI parent summary (editable) */}
          <div className="mt-7 rounded-xl p-5" style={{ backgroundColor: T.grey }} data-testid="report-summary-block">
            <div className="flex items-center justify-between mb-2 gap-2">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">How {student.name.split(" ")[0]} is getting on</p>
              {summary && (
                <button data-testid="save-summary-btn" onClick={saveSummary} disabled={savingSummary}
                  className="no-print inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-slate-200 px-3 py-1 text-slate-600 hover:border-[#7FB069] transition disabled:opacity-50">
                  {savingSummary ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save edits
                </button>
              )}
            </div>
            {summary ? (
              <>
                <textarea data-testid="report-summary-input" value={summary} onChange={(e) => setSummary(e.target.value)}
                  rows={Math.max(6, summary.split("\n").length + 2)}
                  className="no-print w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-700 leading-relaxed outline-none focus:border-[#7FB069] resize-y" />
                <p className="hidden print:block text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="report-summary-text">{summary}</p>
                <div className="no-print flex items-center gap-1.5 flex-wrap mt-2.5" data-testid="report-adjust-row">
                  <span className="text-xs text-slate-400 inline-flex items-center gap-1 mr-1">
                    {refining ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} Adjust:
                  </span>
                  {[
                    ["Make it shorter", "Make it about a third shorter while keeping the key points."],
                    ["Warmer tone", "Make the tone warmer and more encouraging."],
                    ["More detail", "Add a little more specific detail about progress and next steps."],
                    ["Simpler language", "Use simpler, plainer language that any parent can easily understand."],
                  ].map(([label, instruction]) => (
                    <button key={label} data-testid={`adjust-${label.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => refine(instruction)} disabled={refining}
                      className="text-xs font-semibold rounded-full border border-slate-200 px-3 py-1 text-slate-600 hover:border-[#9A7AA0] hover:text-[#7a5e80] transition disabled:opacity-50">
                      {label}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-400 no-print">
                Click <span className="font-semibold">Generate with AI</span> to write a warm, parent-friendly summary — you can edit it before printing or sharing.
              </p>
            )}
          </div>

          {/* Priority targets */}
          <div className="mt-8">
            <h2 className="font-['Outfit'] text-lg font-bold flex items-center gap-2 mb-3" style={{ color: T.slate }}>
              <Target size={18} style={{ color: T.greenDeep }} /> What we're working on
            </h2>
            {targets.length === 0 ? (
              <p className="text-sm text-slate-400">No priority targets set yet.</p>
            ) : (
              <div className="space-y-3" data-testid="report-targets">
                {targets.map((t, idx) => {
                  const meta = SUBJECT_META_ALL[t.subject];
                  const dom = subjects[t.subject]?.domains.find((d) => d.key === t.domain);
                  const prog = PROGRESS_LABEL[t.progress || "not_achieved"];
                  return (
                    <div key={t.id} data-testid={`report-target-${idx}`} className="rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between gap-3 mb-1.5 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: meta?.tint }}>
                          {meta?.name || t.subject}{dom ? ` · ${dom.name}` : ""}
                        </span>
                        <span className="text-xs font-semibold rounded-full px-2.5 py-1 text-white" style={{ backgroundColor: prog.color }}>
                          {prog.label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{t.text}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <p className="text-xs text-slate-400 mt-9 pt-5 text-center" style={{ borderTop: `1px solid ${T.grey}` }}>
            {school.name || "BloomTrack"} · End-of-Term Progress Report · Generated with BloomTrack
          </p>
        </div>
      </div>
    </div>
  );
}
